import { Button } from '@emailforge/ui';
import { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import { Icon } from '@/components/ui/icons';
import { Logo } from '@/components/ui/Logo';
import { setActiveOrganization, signedOut } from '@/features/auth/authSlice';
import { useGetCurrentOrgQuery } from '@/features/organizations/organizationsApi';
import { useSendsStatsQuery } from '@/features/sends/sendsApi';
import { clearIdentity } from '@/lib/monitoring';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

type SidebarItemProps = {
  icon: React.ReactNode;
  label: string;
  to: string;
  end?: boolean;
  count?: string | number;
  kbd?: string;
};

function SidebarItem({ icon, label, to, end, count, kbd }: SidebarItemProps) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => 'mf-nav-item' + (isActive ? ' is-active' : '')}>
      {({ isActive }) => (
        <>
          <span style={{ color: isActive ? 'var(--ink)' : 'var(--ink-3)' }}>{icon}</span>
          <span>{label}</span>
          {count != null && <span className="count mono">{count}</span>}
          {kbd && <span className="kbd mono">{kbd}</span>}
        </>
      )}
    </NavLink>
  );
}

export function AppSidebar() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((s) => s.auth.user);
  const memberships = useAppSelector((s) => s.auth.memberships);
  const activeOrgId = useAppSelector((s) => s.auth.activeOrganizationId);
  const refreshToken = useAppSelector((s) => s.auth.refreshToken);

  const { data: org } = useGetCurrentOrgQuery();
  const { data: sendStats } = useSendsStatsQuery();

  const [showSwitcher, setShowSwitcher] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const switcherRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showSwitcher && !showAccount) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (accountMenuRef.current && !accountMenuRef.current.contains(t)) setShowAccount(false);
      if (switcherRef.current && !switcherRef.current.contains(t)) setShowSwitcher(false);
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [showSwitcher, showAccount]);

  const workspace = org?.name ?? '…';
  const plan = org ? `emailforge · ${org.planCode}` : 'emailforge';

  const userName = user?.name ?? user?.email ?? '—';
  const userInitials = initialsFrom(user?.name ?? user?.email ?? '');
  const myMembership = memberships.find((m) => m.organizationId === activeOrgId);
  const userRole = myMembership?.role ?? '';

  const sendsCount = formatCount(
    (sendStats?.delivered ?? 0) + (sendStats?.opened ?? 0) + (sendStats?.clicked ?? 0),
  );

  const onSwitchOrg = (orgId: string) => {
    dispatch(setActiveOrganization(orgId));
    setShowSwitcher(false);
    window.location.reload();
  };

  const onSignOut = async () => {
    // Best-effort: tell the server to revoke the refresh token so even a
    // leaked access token can't be paired with it later. Don't block sign-out
    // on this — if the request fails we still wipe local state.
    if (refreshToken) {
      try {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
      } catch {
        // ignore
      }
    }
    clearIdentity();
    dispatch(signedOut());
    navigate('/login');
  };

  return (
    <div className="mf-sidebar" style={{ width: 224 }}>
      <div className="mf-sidebar-head" style={{ position: 'relative' }} ref={switcherRef}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <Logo size={22} />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{workspace}</span>
            <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>
              {plan}
            </span>
          </div>
        </div>
        <Button variant="ghost" size="sm" icon aria-label="Switch workspace"
          onClick={() => setShowSwitcher((v) => !v)}
        >
          <Icon.chevDown />
        </Button>

        {showSwitcher && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              left: 8,
              right: 8,
              background: 'var(--paper)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--r-3)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              padding: 6,
              zIndex: 20,
            }}
          >
            <div
              className="mono"
              style={{
                fontSize: 10,
                color: 'var(--ink-3)',
                padding: '6px 8px',
                textTransform: 'uppercase',
                letterSpacing: 0.08,
              }}
            >
              Workspaces
            </div>
            {memberships.map((m) => {
              const active = m.organizationId === activeOrgId;
              return (
                <button
                  key={m.organizationId}
                  onClick={() => onSwitchOrg(m.organizationId)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    width: '100%',
                    padding: '6px 8px',
                    border: 'none',
                    background: active ? 'var(--paper-3)' : 'transparent',
                    borderRadius: 'var(--r-2)',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 4,
                      background: active ? 'var(--flame)' : 'var(--paper-4)',
                      color: active ? 'white' : 'var(--ink-3)',
                      fontSize: 9,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {m.organization.name.slice(0, 1).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 500 }}>{m.organization.name}</div>
                    <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>
                      {m.role}
                    </div>
                  </div>
                  {active && <Icon.check size={12} />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="mf-nav" style={{ flex: 1 }}>
        <div className="mf-nav-section">Workspace</div>
        <SidebarItem icon={<Icon.grid />} label="Delivery Health" to="/app" end kbd="G D" />
        <SidebarItem
          icon={<Icon.mail />}
          label="Templates"
          to="/app/templates"
          count={formatCount(org?.counts.templates ?? 0)}
        />
        <SidebarItem icon={<Icon.folder />} label="Gallery" to="/app/gallery" />
        <SidebarItem icon={<Icon.send />} label="Sends" to="/app/sends" count={sendsCount} />
        <SidebarItem
          icon={<Icon.ab />}
          label="Experiments"
          to="/app/ab"
          count={formatCount(org?.counts.runningExperiments ?? 0)}
        />

        <div className="mf-nav-section" style={{ marginTop: 6 }}>
          Configure
        </div>
        <SidebarItem icon={<Icon.palette />} label="Design tokens" to="/app/tokens" />
        <SidebarItem icon={<Icon.plug />} label="SMTP providers" to="/app/smtp" />
        <SidebarItem icon={<Icon.mail />} label="Senders" to="/app/senders" />
        <SidebarItem icon={<Icon.code />} label="API keys" to="/app/api-keys" />
        <SidebarItem icon={<Icon.layers />} label="Integrations" to="/app/integrations" />
        <SidebarItem icon={<Icon.link />} label="Tracking domains" to="/app/tracking-domains" />

        <div className="mf-nav-section" style={{ marginTop: 6 }}>
          Team
        </div>
        <SidebarItem icon={<Icon.users />} label="Members" to="/app/settings" />
        <SidebarItem icon={<Icon.gear />} label="Settings" to="/app/settings" />
      </div>

      <div
        ref={accountMenuRef}
        style={{
          position: 'relative',
          padding: 10,
          borderTop: '1px solid var(--line-soft)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: 'oklch(0.72 0.09 45)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10.5,
            fontWeight: 600,
          }}
        >
          {userInitials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {userName}
          </div>
          <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>
            {userRole}
          </div>
        </div>
        <Button variant="ghost" size="sm" icon aria-label="Account"
          onClick={() => setShowAccount((v) => !v)}
        >
          <Icon.gear />
        </Button>

        {showAccount && (
          <div
            style={{
              position: 'absolute',
              bottom: 'calc(100% + 4px)',
              left: 8,
              right: 8,
              background: 'var(--paper)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--r-3)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              padding: 6,
              zIndex: 20,
            }}
          >
            <AccountMenuItem
              icon={<Icon.gear size={12} />}
              label="Settings"
              onClick={() => {
                setShowAccount(false);
                navigate('/app/settings');
              }}
            />
            <AccountMenuItem
              icon={<Icon.folder size={12} />}
              label="Export my data (GDPR)"
              onClick={() => {
                setShowAccount(false);
                navigate('/app/settings');
              }}
            />
            <div style={{ height: 1, background: 'var(--line-soft)', margin: '4px 0' }} />
            <AccountMenuItem
              icon={<Icon.arrow size={12} />}
              label="Sign out"
              onClick={onSignOut}
              danger
            />
          </div>
        )}
      </div>
    </div>
  );
}

function AccountMenuItem({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '6px 8px',
        border: 'none',
        background: 'transparent',
        borderRadius: 'var(--r-2)',
        cursor: 'pointer',
        textAlign: 'left',
        color: danger ? 'var(--err)' : 'var(--ink)',
        fontSize: 12.5,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--paper-3)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <span style={{ color: danger ? 'var(--err)' : 'var(--ink-3)' }}>{icon}</span>
      <span>{label}</span>
    </button>
  );
}

function initialsFrom(s: string): string {
  const trimmed = s.trim();
  if (!trimmed) return '–';
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    const first = parts[0]?.[0] ?? '';
    const last = parts[parts.length - 1]?.[0] ?? '';
    return (first + last).toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}m`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}
